"use client";

import { appStore } from "@/app/store";
import { useChat } from "@ai-sdk/react";
import clsx from "clsx";
import { cn, generateUUID, truncateString } from "lib/utils";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { ChatGreeting } from "./chat-greeting";
import { ErrorMessage, PreviewMessage } from "./message";
import PromptInput from "./prompt-input";
import { BugReportModal } from "./bug-report-modal";
import { BugReportContext, BugReportService } from "@/lib/services/bug-report-service";

import { UIMessage } from "ai";
import { useShallow } from "zustand/shallow";

import { deleteThreadAction } from "@/app/api/chat/actions";
import { useToRef } from "@/hooks/use-latest";
import {
  ChatApiSchemaRequestBody,
  ChatMessageAnnotation,
} from "app-types/chat";
import type { Agent } from "app-types/agent";
import { Shortcuts, isShortcutEvent } from "lib/keyboard-shortcuts";
import { AgentNavigation } from "lib/navigation/agent-navigation";
import { useAgentNavigation } from "@/hooks/use-agent-navigation";
import { Loader } from "lucide-react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { mutate } from "swr";
import { safe } from "ts-safe";
import { Button } from "ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";

type Props = {
  threadId: string;
  initialMessages: Array<UIMessage>;
  selectedChatModel?: string;
  agent?: Agent; // New prop for agent-specific chats
  slots?: {
    emptySlot?: ReactNode;
    inputBottomSlot?: ReactNode;
    agentSelectorSlot?: ReactNode; // New slot for agent selection
  };
};

export default function ChatBot({ threadId, initialMessages, agent, slots }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [thinking, setThinking] = useState(false);
  const [bugReportContext, setBugReportContext] = useState<BugReportContext | null>(null);
  const [bugReportModalOpen, setBugReportModalOpen] = useState(false);
  const { handleAgentThreadCreated } = useAgentNavigation();

  const [
    appStoreMutate,
    model,
    toolChoice,
    allowedAppDefaultToolkit,
    allowedMcpServers,
    threadList,
  ] = appStore(
    useShallow((state) => [
      state.mutate,
      state.chatModel,
      state.toolChoice,
      state.allowedAppDefaultToolkit,
      state.allowedMcpServers,
      state.threadList,
    ]),
  );

  // Override permissions with agent-specific permissions if agent is provided
  const effectiveAllowedAppDefaultToolkit = useMemo(() => {
    if (agent?.instructions?.mentions) {
      // If agent has specific tool mentions, use those
      const toolMentions = agent.instructions.mentions
        .filter((m) => m.type === "defaultTool")
        .map((m) => m.name);
      return toolMentions.length > 0 ? toolMentions : allowedAppDefaultToolkit;
    }
    return allowedAppDefaultToolkit;
  }, [agent, allowedAppDefaultToolkit]);

  const effectiveAllowedMcpServers = useMemo(() => {
    if (agent?.instructions?.mentions) {
      // If agent has specific MCP server mentions, use those
      const mcpMentions = agent.instructions.mentions
        .filter((m) => m.type === "tool" || m.type === "mcpServer")
        .reduce((acc, mention) => {
          if (mention.type === "tool" && mention.serverId) {
            acc[mention.serverId] = { tools: [] };
          } else if (mention.type === "mcpServer" && mention.serverId) {
            acc[mention.serverId] = { tools: [] };
          }
          return acc;
        }, {} as Record<string, { tools?: string[] }>);

      return Object.keys(mcpMentions).length > 0 ? mcpMentions : allowedMcpServers;
    }
    return allowedMcpServers;
  }, [agent, allowedMcpServers]);

  const {
    messages,
    input,
    setInput,
    append,
    status,
    reload,
    setMessages,
    addToolResult,
    error,
    stop,
  } = useChat({
    id: threadId,
    api: "/api/chat",
    initialMessages,
    experimental_prepareRequestBody: ({ messages }) => {
      // Update URL based on agent context
      // Keep users at '/' for the main chat to avoid redirecting to /chat/{threadId}
      if (agent) {
        const newUrl = AgentNavigation.getAgentThreadUrl(agent.id, threadId);
        window.history.replaceState({}, "", newUrl);
      }

      const lastMessage = messages.at(-1)!;
      vercelAISdkV4ToolInvocationIssueCatcher(lastMessage);
      const request: ChatApiSchemaRequestBody = {
        id: latestRef.current.threadId,
        chatModel: latestRef.current.model,
        toolChoice: latestRef.current.toolChoice,
        allowedAppDefaultToolkit: latestRef.current.allowedAppDefaultToolkit,
        allowedMcpServers: latestRef.current.allowedMcpServers,
        thinking: latestRef.current.thinking,
        message: lastMessage,
        agentId: agent?.id, // Include agent ID in request
      };
      return request;
    },
    sendExtraMessageFields: true,
    generateId: generateUUID,
    experimental_throttle: 100,
    onFinish() {
      if (threadList[0]?.id !== threadId) {
        mutate("threads");
      }

      // Handle agent thread creation
      if (agent) {
        handleAgentThreadCreated(agent, threadId);
      }
    },
    onError: (error) => {
      console.error("[Chat Error]", error);

      // Log additional debugging info
      console.error("[Chat Error Details]", {
        message: error.message,
        stack: error.stack,
        name: error.name,
        agent: agent?.id,
        threadId
      });

      // Prepare bug report context (we'll get user info from the API call since this is client-side)
      const errorContext: BugReportContext = {
        error,
        userId: 'will-be-filled-by-api', // This will be filled by the API endpoint
        threadId,
        agentId: agent?.id,
        modelProvider: model?.provider || model?.model,
        recentMessages: messages.slice(-5) // Last 5 messages for context
      };
      setBugReportContext(errorContext);

      toast.error(
        truncateString(error.message, 100) ||
        "An error occured, please try again!",
      );
    },
  });

  const [isDeleteThreadPopupOpen, setIsDeleteThreadPopupOpen] = useState(false);

  const latestRef = useToRef({
    toolChoice,
    model,
    allowedAppDefaultToolkit: effectiveAllowedAppDefaultToolkit,
    allowedMcpServers: effectiveAllowedMcpServers,
    messages,
    threadId,
    thinking,
  });

  const handleThinkingChange = useCallback((thinking: boolean) => {
    setThinking(thinking);
  }, []);

  const isLoading = useMemo(
    () => status === "streaming" || status === "submitted",
    [status],
  );

  const emptyMessage = useMemo(
    () => messages.length === 0 && !error,
    [messages.length, error],
  );

  const isInitialThreadEntry = useMemo(
    () =>
      initialMessages.length > 0 &&
      initialMessages.at(-1)?.id === messages.at(-1)?.id,
    [initialMessages, messages],
  );

  const needSpaceClass = useCallback(
    (index: number) => {
      if (error || isInitialThreadEntry || index != messages.length - 1)
        return false;
      const message = messages[index];
      if (message.role === "user") return false;
      return true;
    },
    [messages, error],
  );

  const [isExecutingProxyToolCall, setIsExecutingProxyToolCall] =
    useState(false);

  const isPendingToolCall = useMemo(() => {
    if (status != "ready") return false;
    const lastMessage = messages.at(-1);
    if (lastMessage?.role != "assistant") return false;
    const annotation = lastMessage.annotations?.at(-1) as ChatMessageAnnotation;
    if (annotation?.toolChoice != "manual") return false;
    const lastPart = lastMessage.parts.at(-1);
    if (!lastPart) return false;
    if (lastPart.type != "tool-invocation") return false;
    if (lastPart.toolInvocation.state != "call") return false;
    return true;
  }, [status, messages]);

  const proxyToolCall = useCallback(
    (answer: boolean) => {
      if (!isPendingToolCall) throw new Error("Tool call is not supported");
      setIsExecutingProxyToolCall(true);
      return safe(async () => {
        const lastMessage = messages.at(-1)!;
        const lastPart = lastMessage.parts.at(-1)! as Extract<
          UIMessage["parts"][number],
          { type: "tool-invocation" }
        >;
        return addToolResult({
          toolCallId: lastPart.toolInvocation.toolCallId,
          result: answer,
        });
      })
        .watch(() => setIsExecutingProxyToolCall(false))
        .unwrap();
    },
    [isPendingToolCall, addToolResult],
  );

  useEffect(() => {
    appStoreMutate({ currentThreadId: threadId });
    return () => {
      appStoreMutate({ currentThreadId: null });
    };
  }, [threadId]);

  useEffect(() => {
    if (isInitialThreadEntry)
      containerRef.current?.scrollTo({
        top: containerRef.current?.scrollHeight,
        behavior: "instant",
      });
  }, [isInitialThreadEntry]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const messages = latestRef.current.messages;
      if (messages.length === 0) return;
      const isLastMessageCopy = isShortcutEvent(e, Shortcuts.lastMessageCopy);
      const isDeleteThread = isShortcutEvent(e, Shortcuts.deleteThread);
      if (!isDeleteThread && !isLastMessageCopy) return;
      e.preventDefault();
      e.stopPropagation();
      if (isLastMessageCopy) {
        const lastMessage = messages.at(-1);
        const lastMessageText = lastMessage!.parts
          .filter((part) => part.type == "text")
          ?.at(-1)?.text;
        if (!lastMessageText) return;
        navigator.clipboard.writeText(lastMessageText);
        toast.success("Last message copied to clipboard");
      }
      if (isDeleteThread) {
        setIsDeleteThreadPopupOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div
      className={cn(
        emptyMessage && "justify-center pb-24",
        "flex flex-col min-w-0 relative h-full",
      )}
    >
      {/* Agent selector slot */}
      {slots?.agentSelectorSlot}

      {/* Agent indicator for active conversations */}
      {agent && !emptyMessage && (
        <div className="flex items-center gap-2 px-6 py-2 bg-muted/50 border-b">
          {agent.icon && (
            <span className="text-lg" style={agent.icon.style}>
              {agent.icon.value}
            </span>
          )}
          <span className="text-sm font-medium">
            {agent.name}
          </span>
          {agent.description && (
            <span className="text-xs text-muted-foreground">
              • {agent.description}
            </span>
          )}
        </div>
      )}
      {emptyMessage ? (
        slots?.emptySlot ? (
          slots.emptySlot
        ) : (
          <ChatGreeting agent={agent} />
        )
      ) : (
        <>
          <div
            className={"flex flex-col gap-2 overflow-y-auto py-6"}
            ref={containerRef}
          >
            {messages.map((message, index) => {
              const isLastMessage = messages.length - 1 === index;
              return (
                <PreviewMessage
                  threadId={threadId}
                  messageIndex={index}
                  key={index}
                  message={message}
                  status={status}
                  onPoxyToolCall={
                    isLastMessage &&
                      isPendingToolCall &&
                      !isExecutingProxyToolCall
                      ? proxyToolCall
                      : undefined
                  }
                  isLoading={isLoading || isPendingToolCall}
                  isError={!!error && isLastMessage}
                  isLastMessage={isLastMessage}
                  setMessages={setMessages}
                  reload={reload}
                  className={needSpaceClass(index) ? "min-h-[55dvh]" : ""}
                />
              );
            })}
            {status === "submitted" && messages.at(-1)?.role === "user" && (
              <div className="min-h-[calc(55dvh-56px)]" />
            )}
            {error && (
              <ErrorMessage
                error={error}
                onBugReport={() => setBugReportModalOpen(true)}
              />
            )}
            <div className="min-w-0 min-h-52" />
          </div>
        </>
      )}
      <div className={clsx(messages.length && "absolute bottom-14", "w-full")}>
        <PromptInput
          input={input}
          append={append}
          setInput={setInput}
          isLoading={isLoading || isPendingToolCall}
          onStop={stop}
          thinking={thinking}
          onThinkingChange={handleThinkingChange}
        />
        {slots?.inputBottomSlot}
      </div>
      <DeleteThreadPopup
        threadId={threadId}
        onClose={() => setIsDeleteThreadPopupOpen(false)}
        open={isDeleteThreadPopupOpen}
        agent={agent}
      />
      {bugReportContext && (
        <BugReportModal
          isOpen={bugReportModalOpen}
          onClose={() => {
            setBugReportModalOpen(false);
            setBugReportContext(null);
          }}
          errorContext={bugReportContext}
        />
      )}
    </div>
  );
}

function vercelAISdkV4ToolInvocationIssueCatcher(message: UIMessage) {
  if (message.role != "assistant") return;
  const lastPart = message.parts.at(-1);
  if (lastPart?.type != "tool-invocation") return;
  if (!message.toolInvocations)
    message.toolInvocations = [lastPart.toolInvocation];
}

function DeleteThreadPopup({
  threadId,
  onClose,
  open,
  agent,
}: { threadId: string; onClose: () => void; open: boolean; agent?: Agent }) {
  const t = useTranslations();
  const [isDeleting, setIsDeleting] = useState(false);
  const router = useRouter();
  const handleDelete = useCallback(() => {
    setIsDeleting(true);
    safe(() => deleteThreadAction(threadId))
      .watch(() => setIsDeleting(false))
      .ifOk(() => {
        toast.success(t("Chat.Thread.threadDeleted"));
        // Navigate back to appropriate context (agent or main chat)
        const redirectUrl = agent
          ? AgentNavigation.getAgentChatUrl(agent.id)
          : AgentNavigation.getMainChatUrl();
        router.push(redirectUrl);
      })
      .ifFail(() => toast.error(t("Chat.Thread.failedToDeleteThread")))
      .watch(() => onClose());
  }, [threadId, router]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Chat.Thread.deleteChat")}</DialogTitle>
          <DialogDescription>
            {t("Chat.Thread.areYouSureYouWantToDeleteThisChatThread")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("Common.cancel")}
          </Button>
          <Button variant="destructive" onClick={handleDelete} autoFocus>
            {t("Common.delete")}
            {isDeleting && <Loader className="size-3.5 ml-2 animate-spin" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
